/**
 * SharePointMatterFeeEarner
 *
 * Shown only when a SharePoint-sourced (pre-CM) matter is picked in the
 * invoice / payment flow. Such a matter exists only as a SharePoint folder —
 * it has no CaseMinder record and therefore no known fee earner — so the flow
 * must PROMPT the user to choose one (pre-filled to the current logged-in
 * user) before proceeding. Never silently assign, never leave blank.
 * CM-native matters already carry a fee earner and skip this entirely.
 *
 * Props:
 *   source:   result.source from the matter picker ("sharepoint" | else)
 *   value:    currently chosen fee earner name (parent formData)
 *   onChange: (name) => void — write the chosen fee earner back to parent
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { UserPlus } from "lucide-react";
import { Label } from "./ui/label";
import { API } from "../config/api";

export default function SharePointMatterFeeEarner({ source, value, onChange }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const isSharePoint = source === "sharepoint";

  useEffect(() => {
    if (!isSharePoint) return;
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/lookup/fee-earners`)
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res?.data) ? res.data : [];
        setOptions(list);
        // Pre-fill to the current logged-in user (match on email), but only
        // when the parent has no fee earner yet — never clobber a manual pick.
        if (!value) {
          const email = (
            sessionStorage.getItem("bonsai_user_email") ||
            localStorage.getItem("bonsai_user_email") ||
            ""
          ).toLowerCase();
          const me = list.find((o) => (o.email || "").toLowerCase() === email);
          if (me && onChange) onChange(me.name);
        }
      })
      .catch(() => {
        if (!cancelled) setOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSharePoint]);

  if (!isSharePoint) return null;

  return (
    <div
      className="mt-3 p-3 rounded-md border border-amber-500/40 bg-status-amber-dim/30"
      data-testid="sp-fee-earner-block"
    >
      <Label htmlFor="sp_fee_earner" className="text-sm font-medium flex items-center gap-2">
        <UserPlus className="w-4 h-4" />
        Responsible fee earner <span className="text-status-red">*</span>
      </Label>
      <p className="text-xs text-gray-600 mt-1">
        This matter was found in SharePoint and has no CaseMinder record yet, so no
        fee earner is on file — please confirm who is responsible (pre-filled to you)
      </p>
      <select
        id="sp_fee_earner"
        data-testid="sp-fee-earner-select"
        value={value || ""}
        disabled={loading}
        onChange={(e) => onChange && onChange(e.target.value)}
        className="mt-2 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
      >
        <option value="">{loading ? "Loading fee earners…" : "— pick a fee earner —"}</option>
        {options.map((o) => (
          <option key={o.email || o.name} value={o.name}>
            {o.name}
            {o.position ? ` · ${o.position}` : ""}
          </option>
        ))}
      </select>
    </div>
  );
}
